import React, { useMemo } from 'react';
import WordCloud from 'react-d3-cloud';
import { FiHeart } from 'react-icons/fi';
import styles from './PollResults.module.css';

// Type-aware results view for a poll's active question - shared by the
// instructor's Live Poll Board and the student's Join a Poll page (shown
// once a student has submitted their own answer), so results look and
// behave identically everywhere they're displayed.
function PollResults({
  activeQuestion,
  optionTallies = [],
  ratingStats = null,
  wordCloudData = [],
  answers = [],
  onVote,
  wordCloudWidth = 560,
  wordCloudHeight = 320,
}) {
  const wordCloudWords = useMemo(
    () => wordCloudData.map((w) => ({ text: w.text, value: w.count })),
    [wordCloudData]
  );
  const maxWordCount = Math.max(1, ...wordCloudData.map((w) => w.count));
  const wordCloudFontSize = (word) => 14 + (word.value / maxWordCount) * 50;

  if (!activeQuestion) return null;

  if (activeQuestion.question_type === 'multiple_choice') {
    const maxCount = Math.max(1, ...optionTallies.map((t) => t.count));
    return (activeQuestion.options || []).map((opt) => {
      const count = optionTallies.find((t) => t.optionId === opt.id)?.count || 0;
      return (
        <div key={opt.id} className={styles.barRow}>
          <div className={styles.barRowTop}>
            <span>{opt.text}</span>
            <span className={styles.barRowCount}>{count}</span>
          </div>
          <div className={styles.barTrack}>
            <div className={styles.barFill} style={{ width: `${(count / maxCount) * 100}%` }} />
          </div>
        </div>
      );
    });
  }

  if (activeQuestion.question_type === 'rating') {
    return (
      <>
        <div className={styles.ratingAverage}>
          {ratingStats ? ratingStats.average.toFixed(1) : '—'}{' '}
          <span className={styles.ratingAverageMeta}>avg of {ratingStats?.count || 0}</span>
        </div>
        {ratingStats && [1, 2, 3, 4, 5].map((v) => {
          const c = ratingStats.distribution[v] || 0;
          const max = Math.max(1, ...Object.values(ratingStats.distribution));
          return (
            <div key={v} className={styles.distributionRow}>
              <span className={styles.distributionLabel}>{v}</span>
              <div className={styles.distributionTrack}>
                <div className={styles.barFill} style={{ width: `${(c / max) * 100}%` }} />
              </div>
              <span className={styles.distributionCount}>{c}</span>
            </div>
          );
        })}
      </>
    );
  }

  if (activeQuestion.question_type === 'word_cloud') {
    if (wordCloudWords.length === 0) return <div className={styles.emptyState}>No answers yet.</div>;
    return (
      <div key={wordCloudWords.length} className={styles.wordCloudFade}>
        <WordCloud
          data={wordCloudWords}
          width={wordCloudWidth}
          height={wordCloudHeight}
          font="Inter"
          fontWeight="700"
          fontSize={wordCloudFontSize}
          padding={3}
          rotate={0}
          random={() => 0.5}
          fill="var(--primary-color)"
        />
      </div>
    );
  }

  if (activeQuestion.question_type === 'ideas') {
    if (answers.length === 0) return <div className={styles.emptyState}>No ideas yet.</div>;
    return (
      <div className={styles.answerList}>
        {answers.map((a) => (
          <div key={a.id} className={styles.ideaRow}>
            <div className={styles.answerRowText}>{a.answer_text}</div>
            {onVote ? (
              <button type="button" className={styles.ideaVoteCount} style={{ cursor: 'pointer', border: 'none', background: 'none' }} onClick={() => onVote(a.id)}>
                <FiHeart size={14} />
                {a.voteCount || 0}
              </button>
            ) : (
              <span className={styles.ideaVoteCount}>
                <FiHeart size={14} />
                {a.voteCount || 0}
              </span>
            )}
          </div>
        ))}
      </div>
    );
  }

  // open_text
  if (answers.length === 0) return <div className={styles.emptyState}>No answers yet.</div>;
  return (
    <div className={styles.answerList}>
      {answers.map((a) => (
        <div key={a.id} className={styles.answerRow}>
          {a.group_label && <div className={styles.answerRowMeta}>{a.group_label}</div>}
          <div className={styles.answerRowText}>{a.answer_text}</div>
        </div>
      ))}
    </div>
  );
}

export default PollResults;
